import React from 'react';
import './DailyQuiz.css';

type AnalysisResult = {
  similarity_analysis?: any;
  returns_analysis?: any;
  ai_analysis?: any;
};

export const AnalysisDashboard: React.FC<{
  result: AnalysisResult;
  onSelectSimilar?: (segment: any) => void;
}> = ({ result, onSelectSimilar }) => {
  const aiText: string | undefined = result?.ai_analysis?.analysis;
  const returns = result?.returns_analysis?.returns_analysis || {};
  const segments: any[] = result?.similarity_analysis?.similar_segments || [];
  const fmtDay = (ms?: number) => {
    if (!ms) return '';
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      }).format(new Date(ms));
    } catch {
      return '';
    }
  };

  const metrics: any = (result as any)?.extra_metrics || null;
  const getStyles = (grade?: string) => {
    switch (grade) {
      case 'good':
        return {
          border: '1px solid rgba(16,185,129,0.45)',
          background: 'rgba(16,185,129,0.06)',
          color: '#d1fae5'
        };
      case 'warning':
        return {
          border: '1px solid rgba(245,158,11,0.45)',
          background: 'rgba(245,158,11,0.08)',
          color: '#fde68a'
        };
      case 'danger':
        return {
          border: '1px solid rgba(239,68,68,0.45)',
          background: 'rgba(239,68,68,0.08)',
          color: '#fecaca'
        };
      default:
        return {
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(31,41,55,0.5)',
          color: '#e5e7eb'
        };
    }
  };

  return (
    <div className="dq-results" style={{ marginTop: 16 }}>
      <div className="dq-panel" style={{ gridColumn: '1 / -1' }}>
        <h4>AI Summary</h4>
        <p className="dq-note" style={{ fontSize: 13, color: '#e5e7eb' }}>
          {aiText || 'No AI summary available.'}
        </p>
      </div>

      <div className="dq-panel">
        <h4>Forward Returns (stats)</h4>
        {Object.keys(returns).length === 0 ? (
          <p className="dq-note">No return statistics.</p>
        ) : (
          <div className="dq-bars">
            {(() => {
              const entries = Object.entries(returns) as [string, any][];
              const maxAbs =
                entries.reduce((m, [, s]) => {
                  const v = Math.abs(Number(s?.mean_return ?? 0));
                  return v > m ? v : m;
                }, 0.0001); // avoid div-by-zero
              return entries.map(([period, stats]) => {
                const mean = Number(stats?.mean_return ?? 0);
                // Scale bar length relative to the max absolute mean so bars are visually comparable
                const rel = Math.abs(mean) / maxAbs;
                const width = Math.min(95, Math.max(6, rel * 90)); // keep some padding
              const color =
                mean >= 0
                  ? 'linear-gradient(90deg,#10b98188,#10b98133)'
                  : 'linear-gradient(90deg,#ef444488,#ef444433)';
                return (
                  <div className="dq-bar" key={period}>
                    <span>T+{period}</span>
                    <div className="track">
                      <div className="fill" style={{ width: `${width}%`, background: color }} />
                    </div>
                    <span>{mean.toFixed(2)}%</span>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      <div className="dq-panel">
        <h4>Pattern Metrics</h4>
        {!metrics ? (
          <p className="dq-note">No metrics.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ padding: 12, borderRadius: 10, ...getStyles(metrics?.average_return?.grade) }}>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Average Return</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {typeof metrics?.average_return?.value_pct === 'number'
                  ? `${metrics.average_return.value_pct.toFixed(2)}%`
                  : '--'}
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, ...getStyles(metrics?.win_rate?.grade) }}>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Win Rate</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {typeof metrics?.win_rate?.value_pct === 'number'
                  ? `${metrics.win_rate.value_pct.toFixed(1)}%`
                  : '--'}
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, ...getStyles(metrics?.volatility?.grade) }}>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Volatility</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {metrics?.volatility?.value ?? '--'}
              </div>
              {metrics?.volatility?.label && (
                <div style={{ display: 'inline-block', marginTop: 6, padding: '4px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                  {metrics.volatility.label}
                </div>
              )}
            </div>
            <div style={{ padding: 12, borderRadius: 10, ...getStyles(metrics?.trend_suggestion?.grade) }}>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Trend Suggestion</div>
              <div style={{ fontSize: 20, fontWeight: 700, textTransform: 'capitalize' }}>
                {metrics?.trend_suggestion?.label ?? '--'}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="dq-panel" style={{ gridColumn: '1 / -1' }}>
        <h4>Top Similar Segments</h4>
        {segments.length === 0 ? (
          <p className="dq-note">No similar segments found.</p>
        ) : (
          <div className="dq-similar-grid">
            {segments.slice(0, 6).map((seg, idx) => {
              const rho = Number(seg.correlation ?? 0);
              const dateLabel = fmtDay(seg.start_time);
              const rhoClass =
                rho >= 0.9 ? 'strong' : rho >= 0.85 ? 'good' : 'weak';
              return (
                <div key={idx} className="dq-chip">
                  <span className="dq-badge">
                    <span className={`dq-rho ${rhoClass}`}>ρ {rho.toFixed(3)}</span> • {dateLabel}
                  </span>
                  <button
                    className="dq-btn dq-btn-sm"
                    onClick={() => onSelectSimilar?.(seg)}
                    aria-label="View similar segment"
                  >
                    View
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisDashboard;


